import { Button, Flex, useDisclosure } from '@chakra-ui/react';
import React from 'react';
import { useParams, Link } from 'react-router-dom';

import { useEditLoanContext } from '../useEditLoanContext';
import {
  ConfirmEditSubmitModal,
  ConfirmEditApproveModal,
} from './ConfirmEditActionModals';
import { ReviewLoanSettingsMenu } from './ReviewLoanSettingsMenu';
import { z } from 'zod/v4';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';

// Types
const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
});

export const ReviewHeaderBar = () => {
  const params = useParams();
  const { loanId: originalLoanId } = RouteParamsStruct.parse(params);

  const SubmitModalControls = useDisclosure();
  const ApproveModalControls = useDisclosure();

  const context = useEditLoanContext();

  if (context.status !== 'ready') {
    return (
      <Flex
        id="review-header-bar-loading"
        w="100%"
        justify="space-between"
        alignItems="center"
      >
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding', path: '/' },
            { label: 'Loans', path: '/loans' },
            { type: 'loading' },
          ]}
        />
      </Flex>
    );
  }

  const isSubmittedRequest =
    context.state.newEntities?.loanAction.status === 'submitted';

  return (
    <Flex
      id="review-header-bar"
      w="100%"
      justify="space-between"
      alignItems="center"
    >
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Funding', path: '/' },
          { label: 'Loans', path: '/loans' },
          isSubmittedRequest
            ? {
                label: context.state.originalResolvedLoan.name || 'Loan',
                path: `/loans/${originalLoanId}`,
              }
            : {
                label: `Edit ${
                  context.state.originalResolvedLoan.name || 'Loan'
                }`,
                path: `/loans/${originalLoanId}/edit`,
              },
        ]}
      />

      <Flex gap="1rem" alignItems="center">
        {context.state.newEntities !== null ? (
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={ApproveModalControls.onOpen}
          >
            Approve Request
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              as={Link}
              to={`/loans/${originalLoanId}/edit`}
            >
              Return to Editing
            </Button>

            <Button
              variant="primary"
              colorScheme="v2-aurora"
              onClick={SubmitModalControls.onOpen}
            >
              Submit For Approval
            </Button>
          </>
        )}

        <ReviewLoanSettingsMenu />
      </Flex>

      {/* *** Confirmation Modals *** */}
      <ConfirmEditSubmitModal
        modalControls={SubmitModalControls}
        loan={context.state.originalResolvedLoan}
      />
      <ConfirmEditApproveModal modalControls={ApproveModalControls} />
    </Flex>
  );
};
